<template>
  <div class="min-h-screen">
    <Head title="Órdenes" />
    
    <!-- Header Section -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div class="flex items-center justify-between mb-6">
        <div class="flex items-center space-x-4">
          <button 
            class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
            @click="back()"
          >
            <svg class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Volver
          </button>
          <h1 class="text-2xl font-bold text-gray-900">Ventas</h1>
        </div>

        <!-- Action Buttons -->
        <div class="flex items-center space-x-2">
          <Link 
            v-if="user.role === 'financial' || user.role === 'admin'" 
            :href="`/orders/create`"
            class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
          >
            <svg class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            Crear cobranza
          </Link>
          <a 
            :href="`/orders/export`" 
            class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-200"
            download
          >
            <svg class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Exportar órdenes
          </a>
          <a 
            v-if="user.role === 'financial' || user.role === 'admin'"
            href="/ordersDaily" 
            target="_blank"
            class="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200 shadow-sm"
          >
            <svg class="h-4 w-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Cierre Diario (Simple)
          </a>
          <a 
            v-if="user.role === 'financial' || user.role === 'admin'"
            href="/ordersDailyAdvance" 
            target="_blank"
            class="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200 shadow-sm"
          >
            <svg class="h-4 w-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Cierre Diario (Detallado)
          </a>
        </div>
      </div>

      <!-- Search Section -->
      <div class="flex items-center justify-between mb-6">
        <search-filter v-model="form.search" class="w-full max-w-md" @reset="reset">
          <label class="block text-sm font-semibold text-gray-700 mb-1">Filtrar por Estado:</label>
          <select v-model="form.state" class="mt-1 w-full form-select rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
            <option value="">Todos los estados</option>
            <option value="request">Pendiente por Pago</option>
            <option value="finish">Cuota Cancelada (Finalizada)</option>
            <option value="pay">Pagada</option>
            <option value="delivery">En espera de despacho</option>
            <option value="payRevition">Pago en revisión</option>
            <option value="cancel">Anulada</option>
          </select>
        </search-filter>
      </div>

      <!-- Table Section -->
      <div class="bg-white shadow-sm rounded-lg overflow-hidden">
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Concepto</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Propietario</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                <th scope="col" class="relative px-6 py-3">
                  <span class="sr-only">Acciones</span>
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="order in orders.data" 
                  :key="order.id" 
                  :class="[
                    'hover:bg-gray-50 transition-colors duration-150',
                    order.order_state === 'cancel' ? 'bg-red-50' : ''
                  ]"
              >
                <td class="px-6 py-4 whitespace-nowrap">
                  <Link :href="`orders/${order.id}`" class="text-sm font-medium text-indigo-600 hover:text-indigo-900">
                    {{ order.number_order }}
                  </Link>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {{ order.date }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span 
                    :class="[
                      'px-2 inline-flex text-xs leading-5 font-semibold rounded-full',
                      getStatusClass(order.order_state)
                    ]"
                  >
                    {{ getStatusText(order.order_state) }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {{ getOrderConcept(order) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {{ order.client.name }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  ${{ order.total }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <Link 
                    :href="`orders/${order.id}`"
                    class="text-indigo-600 hover:text-indigo-900"
                  >
                    <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </td>
              </tr>
              <tr v-if="!orders.data || orders.data.length === 0">
                <td colspan="7" class="px-6 py-4 text-center text-sm text-gray-500">
                  No hay órdenes registradas.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Pagination -->
      <div class="mt-6">
        <pagination :links="orders.links" />
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import Layout from '@/Shared/Layout'
import { getState } from '@/utils'
import Pagination from '@/Shared/Pagination.vue'
import SearchFilter from '@/Shared/SearchFilter.vue'
import throttle from 'lodash/throttle'
import pickBy from 'lodash/pickBy'

export default {
  components: {
    SearchFilter,
    Head,
    Icon,
    Link,
    Pagination,
  },
  layout: Layout,
  props: {
    orders: Object,
    user: Object,
    filters: Object,
  },
  data() {
    return {
      form: {
        search: this.filters?.search || '',
        state: this.filters?.state || '',
      },
    }
  },
  watch: {
    form: {
      deep: true,
      handler: throttle(function () {
        this.$inertia.get('/orders', pickBy(this.form), { preserveState: true })
      }, 150),
    },
  },
  methods: {
    reset() {
      this.form.search = ''
      this.form.state = ''
    },
    back() {
      window.history.back()
    },
    getStatusText(state) {
      const statusMap = {
        'cancel': 'ANULADA',
        'request': 'PENDIENTE POR PAGO',
        'finish': 'CUOTA PAGADA',
        'pay': 'PAGADA',
        'delivery': 'EN ESPERA DE DESPACHO',
        'payRevition': 'PAGO EN REVISIÓN'
      }
      return statusMap[state] || state
    },
    isDefaultGeneric(text) {
      if (!text) return true
      const generics = ['cobranza rapida', 'cobranza rápida', 'compra rapida!', 'compra rápida!', 'sin observaciones']
      return generics.includes(text.trim().toLowerCase())
    },
    getOrderConcept(order) {
      if (order.products && order.products.length > 0) {
        const names = order.products
          .map(p => (p.product ? p.product.name : (p.name || '')))
          .filter(Boolean)
        if (names.length > 0) {
          return names.join(', ')
        }
      }
      const clean = (order.comentary || '').trim()
      if (clean && !this.isDefaultGeneric(clean)) {
        return clean
      }
      return 'Cuota de condominio'
    },
    hasExtraObservation(order) {
      const clean = (order.comentary || '').trim()
      if (!clean || this.isDefaultGeneric(clean)) {
        return false
      }
      return clean !== this.getOrderConcept(order)
    },
    getStatusClass(state) {
      const classMap = {
        'cancel': 'bg-red-100 text-red-800',
        'request': 'bg-yellow-100 text-yellow-800',
        'finish': 'bg-green-100 text-green-800',
        'pay': 'bg-blue-100 text-blue-800',
        'delivery': 'bg-purple-100 text-purple-800',
        'payRevition': 'bg-indigo-100 text-indigo-800'
      }
      return classMap[state] || 'bg-gray-100 text-gray-800'
    }
  },
}
</script>
